import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { HOME } from "@/constants/testIds";
import { LOGO_URL } from "@/constants/brand";
import { Menu, X, ChevronDown } from "lucide-react";
import { isTombSeason } from "@/data/tombBlessing";
import { FAMILY_SURVEY_URL, GKK_PORTAL_URL, FRANCIS_CONNECT_URL, YOUTH_PULSE_URL, PARISH_ATTENDANCE_URL, PASTORAL_CARE_URL } from "@/data/links";

const topLinks = [
  { id: "home", label: "Home", testId: HOME.navHome },
  { id: "francis-connect", label: "FrancisConnect", testId: "nav-link-francis-connect", to: FRANCIS_CONNECT_URL, external: true },
  { id: "parish-attendance", label: "Parish Attendance", testId: "nav-link-parish-attendance", to: PARISH_ATTENDANCE_URL, external: true },
  { id: "family-survey", label: "Family Survey", testId: "nav-link-family-survey", to: FAMILY_SURVEY_URL, external: true },
  { id: "pastoral-care", label: "Care", testId: "nav-link-pastoral-care", to: PASTORAL_CARE_URL, external: true },
  { id: "gkk-portal", label: "GKK Records", testId: "nav-link-gkk-portal", to: GKK_PORTAL_URL, external: true },
  { id: "youth-pulse", label: "Youth Pulse", testId: "nav-link-youth-pulse", to: YOUTH_PULSE_URL, external: true },
  ...(isTombSeason() ? [{ id: "tomb-blessing", label: "All Souls", testId: "nav-link-tomb-blessing" }] : []),
];

const groups = [
  {
    label: "Our Parish",
    items: [
      { id: "about", label: "About", testId: HOME.navAbout },
      { id: "personnel", label: "Personnel", testId: "nav-link-personnel" },
      { id: "construction", label: "Church Project", testId: HOME.navConstruction },
      { id: "fiesta", label: "Fiesta Gallery", testId: "nav-link-fiesta", to: "/fiesta" },
      { id: "library", label: "Pilgrim Library", testId: "nav-link-library" },
    ],
  },
  {
    label: "Parish Life",
    items: [
      { id: "calendar", label: "Calendar", testId: "nav-link-calendar" },
      { id: "ministries", label: "Ministries", testId: HOME.navMinistries },
      { id: "gkk", label: "GKK Directory", testId: "nav-link-gkk" },
    ],
  },
  {
    label: "Sacraments",
    items: [
      { id: "schedule", label: "Mass Schedule", testId: HOME.navSchedule },
      { id: "sacraments", label: "Sacraments", testId: HOME.navSacraments },
      { id: "intentions", label: "Mass Intentions", testId: "nav-link-intentions" },
    ],
  },
  {
    label: "Services",
    items: [
      { id: "appointments", label: "Appointments", testId: "nav-link-appointments" },
      { id: "contact", label: "Contact", testId: HOME.navContact },
    ],
  },
];

const linkProps = (l) => ({
  href: l.to || `/#${l.id}`,
  ...(l.external && { target: "_blank", rel: "noopener noreferrer" }),
});

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid={`nav-group-${slug(group.label)}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex items-center gap-1 transition-colors ${open ? "text-terracotta" : "text-cocoa-800 hover:text-terracotta"}`}
      >
        {group.label}
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-[opacity,transform] duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="min-w-[200px] bg-parchment-50 border border-parchment-400 shadow-[0_18px_40px_-20px_rgba(60,40,20,0.45)] py-2">
          {group.items.map((l) => (
            <a
              key={l.id}
              {...linkProps(l)}
              data-testid={l.testId}
              onClick={() => setOpen(false)}
              className="block px-5 py-2.5 text-[13px] text-cocoa-800 hover:bg-parchment-200 hover:text-terracotta transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

const TopLink = ({ l }) => (
  <a {...linkProps(l)} data-testid={l.testId} className="text-cocoa-800 hover:text-terracotta transition-colors relative group">
    {l.label}
    <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
  </a>
);

const SurveyStrip = () => (
  <div data-testid="survey-strip" className="bg-gold text-cocoa-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2 flex items-center justify-center sm:justify-between gap-3 font-sans text-[12px]">
      <span className="sm:hidden font-semibold">Parish Family Survey</span>
      <span className="hidden sm:inline tracking-wide">
        <strong className="font-semibold">Parish Family Survey</strong> — help us know and serve every family.
      </span>
      <a
        href={FAMILY_SURVEY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="survey-strip-btn"
        className="inline-flex items-center gap-1.5 rounded-full bg-cocoa-900 text-parchment-100 px-4 py-1 text-[11px] uppercase tracking-[0.18em] hover:bg-terracotta transition-colors"
      >
        Start the Survey →
      </a>
    </div>
  </div>
);

function MobileMenu({ open, close }) {
  const item = (l) => (
    <a key={l.id} {...linkProps(l)} data-testid={`${l.testId}-mobile`} onClick={close} className="text-cocoa-800 py-1.5 block">
      {l.label}
    </a>
  );
  return (
    <div
      className={`min-[1440px]:hidden overflow-hidden transition-all duration-300 bg-parchment-100 border-t border-parchment-400/60 ${
        open ? "max-h-[85vh] overflow-y-auto" : "max-h-0"
      }`}
    >
      <nav className="px-6 py-5 font-sans text-sm">
        <div className="pb-3 border-b border-parchment-300">{topLinks.map(item)}</div>
        {groups.map((g) => (
          <div key={g.label} className="py-3 border-b border-parchment-300">
            <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-1">{g.label}</div>
            <div className="grid grid-cols-2 gap-x-4">{g.items.map(item)}</div>
          </div>
        ))}
        <Link to="/admin" onClick={close} className="block text-cocoa-700 pt-4 text-[11px] uppercase tracking-[0.2em]">
          Admin
        </Link>
      </nav>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <SurveyStrip />

      {/* Top whisper bar */}
      <div className="hidden md:block bg-cocoa-900 text-parchment-100/90 text-[11px] tracking-[0.18em] uppercase">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2.5 flex justify-between font-sans">
          <span>Saint Francis of Assisi Parish · Brgy. Mabuhay · General Santos City</span>
          <span className="text-gold">mabuhayparish.org</span>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-parchment-100/95 backdrop-blur-md border-b border-parchment-400/60"
            : "bg-parchment-100/70 backdrop-blur-sm border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl min-[1440px]:max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <a href="/#home" className="flex items-center gap-3 group flex-shrink-0 mr-4">
            <div className="relative w-12 h-12 rounded-full overflow-hidden border border-cocoa-900/30 bg-parchment-50 flex-shrink-0">
              <img src={LOGO_URL} alt="Saint Francis of Assisi Parish seal" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-cocoa-900 text-base md:text-[17px]">Saint Francis of Assisi Parish</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cocoa-700 font-sans">Mabuhay · Diocese of Marbel</div>
            </div>
          </a>

          <nav className="hidden min-[1440px]:flex items-center gap-4 font-sans text-[13px] tracking-wide whitespace-nowrap">
            {topLinks.map((l) => <TopLink key={l.id} l={l} />)}
            {groups.map((g) => <NavDropdown key={g.label} group={g} />)}
            <Link to="/admin" className="text-[11px] uppercase tracking-[0.2em] text-cocoa-700 border-l border-parchment-400 pl-4 hover:text-terracotta transition-colors">
              Admin
            </Link>
          </nav>

          <button
            data-testid={HOME.mobileMenuToggle}
            onClick={() => setOpen((v) => !v)}
            className="min-[1440px]:hidden p-2 text-cocoa-900"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <MobileMenu open={open} close={() => setOpen(false)} />
      </header>
    </>
  );
}
